import { useRef, useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import maplibregl from 'maplibre-gl';
import moment from 'moment';
import 'moment/min/locales';
import {useLocation} from "react-router-dom";
import SearchBar from '../SearchBar/SearchBar';
import { RightTop } from '../MapLayer/widget-positions/RightTop';
import { SelectLayer } from '../SelectLayer/SelectLayer';

import {StateType} from '../../types/StateType';

// MapBox utils
// https://www.npmjs.com/package/mapbox-gl-utils
// https://github.com/mapbox/mapbox-gl-js/issues/1722#issuecomment-460500411
import U from 'mapbox-gl-utils';
import {getMapStyles, applyMapStyle} from './MapUtils/map';
import {createSafeGeolocateControl} from './MapUtils/mapControls';
import MapAttribution from './MapControls/MapAttribution';
import { whenMapStyleReady } from './MapUtils/mapGuards';
import { applyDataLayerOrderWhenReady } from './MapUtils/dataLayerOrder';
import {initPopupLogic} from './MapUtils/popups.js';
import {initClusters} from './MapUtils/clusters.js';
import {
  addLayers,
  activateLayers
} from './MapUtils/layers';
import {addSources} from './MapUtils/sources.js';
import {
  navigateToGeography,
  triggerGeographyClick,
  fetchPublicZones,
  fetchAdminZones
} from './MapUtils/zones.js';
import {sources} from './sources.js';
import {
  DISPLAYMODE_PARK,
  DISPLAYMODE_RENTALS,
  DISPLAYMODE_START,
  DISPLAYMODE_OTHER,
  DISPLAYMODE_SERVICE_AREAS,
  DISPLAYMODE_POLICY_HUBS,
  DATA_LAYER_ORDER_SERVICE_AREAS,
  DATA_LAYER_ORDER_HUBS,
  DATA_LAYER_ORDER_VERBODSGEBIEDEN,
} from '../../reducers/layers.js';
import { removeHubsFromMap } from './MapUtils/map.policy_hubs';
import { removeServiceAreasFromMap } from './MapUtils/map.service_areas';
import { removeServiceAreaDeltaFromMap } from './MapUtils/map.service_area_delta';

import './MapComponent.css';

import {layers} from './layers';
import {getVehicleMarkers, getVehicleMarkers_rentals} from './vehicle_marker.js';

import IsochroneTools from '../IsochroneTools/IsochroneTools';
import DdH3HexagonLayer from '../MapLayer/DdH3HexagonLayer';
import DdServiceAreasLayer from '../MapLayer/DdServiceAreasLayer';
import DdPolicyHubsLayer from '../MapLayer/DdPolicyHubsLayer';
import DdParkEventsLayer from '../MapLayer/DdParkEventsLayer';
import DdRentalsLayer from '../MapLayer/DdRentalsLayer';
import DdServiceAreasOverlay from '../MapLayer/DdServiceAreasOverlay';
import DdPolicyHubsOverlay from '../MapLayer/DdPolicyHubsOverlay';
import { WidthIcon } from '@radix-ui/react-icons';
import { useBackgroundLayer } from './MapUtils/useBackgroundLayer';
import { updateStreetVisibilityForSatellite } from './MapUtils/backgroundLayerManager';
import { getProviderColorForProvider } from '../../helpers/providers';
import { isOperatorPrestatiesView } from '../../helpers/prestatiesAanbiedersViewMode';
import {
  selectDataLayerOrder,
  selectOverlayLayers,
  isOverlayLayerEnabled
} from '../../helpers/layerSelectors';

// Set language for momentJS
moment.locale('nl');

const MapComponent = (props): JSX.Element => {
  const [pathName, setPathName] = useState(document.location.pathname);
  const [uriParams, setUriParams] = useState(document.location.search);

  const filterGebied = useSelector((state: StateType) => {
    return state.filter ? state.filter.gebied : null;
  });

  const displayMode = useSelector((state: StateType) => {
    return state.layers ? state.layers.displaymode : DISPLAYMODE_PARK;
  });

  const mapStyle = useSelector((state: StateType) => {
    return state.layers ? state.layers.map_style : null;
  });

  const dataLayerOrder = useSelector(selectDataLayerOrder);
  const overlayLayers = useSelector(selectOverlayLayers);

  // Connect to redux store
  const dispatch = useDispatch()

  // Get data from store
  const vehicles = useSelector((state: StateType) => state.vehicles || null);
  const filter = useSelector((state: StateType) => state.filter || null);
  const rentals = useSelector((state: StateType) => state.rentals || null);
  const stateLayers = useSelector((state: StateType) => state.layers || null);
  const isLoggedIn = useSelector((state: StateType) => state.authentication.user_data ? true : false);
  const providers = useSelector((state: StateType) => (state.metadata && state.metadata.aanbieders) ? state.metadata.aanbieders : []);
  const aclOperators = useSelector((state: StateType) =>
    state.metadata?.aclOperators ? state.metadata.aclOperators : []
  );
  const gebieden = useSelector((state: StateType) =>
    state.metadata?.gebieden ? state.metadata.gebieden : []
  );
  const extent/* map boundaries */ = useSelector((state: StateType) => state.layers ? state.layers.extent : null);
  const [counter, setCounter] = useState(0);
  const zones_geodata = useSelector((state: StateType) => {
    if(!state||!state.zones_geodata) {
      return null;
    }
    return state.zones_geodata;
  });
  const viewRentals = useSelector((state: StateType) => state.layers ? state.layers.view_rentals : null);
  const isrentals=displayMode===DISPLAYMODE_RENTALS;

  const mapContainer = useRef(null);

  // Define mapStateTypeainer;
  const [publicZones, setPublicZones] = useState(null);
  const [didMapLoad, setDidMapLoad] = useState(false);
  const [didMapDrawLoad, setDidMapDrawLoad] = useState(false);
  // Fallback: NL overview when there is no persisted map extent (issue #259).
  const [lng] = useState((stateLayers.mapextent && stateLayers.mapextent[0]) ? (stateLayers.mapextent[0] + stateLayers.mapextent[2]) / 2 : 5.29);
  const [lat] = useState((stateLayers.mapextent && stateLayers.mapextent[1]) ? (stateLayers.mapextent[1] + stateLayers.mapextent[3]) / 2 : 52.13);
  const [zoom] = useState(stateLayers.zoom || 7);
  const [didInitSourcesAndLayers, setDidInitSourcesAndLayers] = useState(false);
  const [didAddPublicZones, setDidAddPublicZones] = useState(false);
  const [didAddAdminZones, setDidAddAdminZones] = useState(false);
  const [activeLayers, setActiveLayers] = useState([]);
  const [didApplyMapStyle, setDidApplyMapStyle] = useState(false);
  let map = useRef(null);
  const mapControlsRef = useRef<maplibregl.IControl[]>([]);

  // Initialize hooks after map is declared
  const { getAvailableLayers } = useBackgroundLayer(map.current);

  // Use the background layer hook
  const { setLayer } = useBackgroundLayer(map.current);

  const userData = useSelector((state: StateType) => {
    return state.authentication.user_data;
  });

  const token = useSelector((state: StateType) => {
    if(state.authentication && state.authentication.user_data) {
      return state.authentication.user_data.token;
    }
    return null;
  });

  const filterbarOpen = useSelector((state: StateType) => {
    return state.ui && state.ui.FILTERBAR || false;
  });

  // Store window location in a local variable
  let location = useLocation();
  useEffect(() => {
    setUriParams(location ? location.search : null);
  }, [location]);

  const applyMapSettings = (theMap) => {
    const navigationControl = new maplibregl.NavigationControl({
      showCompass: false
      // showZoom: false
    });
    theMap.addControl(navigationControl, 'bottom-right');
    mapControlsRef.current.push(navigationControl);

    const geolocateControl = createSafeGeolocateControl({
      positionOptions: {
        enableHighAccuracy: true
      },
      trackUserLocation: true
    });
    theMap.addControl(geolocateControl, 'bottom-right');
    mapControlsRef.current.push(geolocateControl);

    // Disable rotating
    theMap.dragRotate.disable();
    theMap.touchZoomRotate.disableRotation();
  }

  const registerMapView = useCallback(theMap => {
    // Add map center & zoom level to URL
    const mapCenter = theMap.getCenter();
    const url = new URL(window.location.href);
    url.searchParams.set('lng', mapCenter.lng);
    url.searchParams.set('lat', mapCenter.lat);
    url.searchParams.set('zoom', theMap.getZoom());
    window.history.replaceState({}, '', url.toString());

    // Store bounds in redux store
    const bounds = theMap.getBounds();
    const payload = [
      bounds._sw.lng,
      bounds._sw.lat,
      bounds._ne.lng,
      bounds._ne.lat
    ];
    dispatch({ type: 'LAYER_SET_MAP_EXTENT', payload: payload })
    dispatch({ type: 'LAYER_SET_MAP_ZOOM', payload: theMap.getZoom() })
  }, []);

  // Init MapLibre map
  // Docs: https://maptiler.zendesk.com/hc/en-us/articles/4405444890897-Display-MapLibre-GL-JS-map-using-React-JS
  // const mapcurrent_exists = map.current!==undefined;
  useEffect(() => {
    const initMap = () => {
      // Stop if map exists already
      if (map.current) return;

      const mapStyles = getMapStyles();

      map.current = new maplibregl.Map({
        container: mapContainer.current,
        // @ts-ignore
        style: mapStyles.base,
        // @ts-ignore
        accessToken: process ? process.env.REACT_APP_MAPBOX_TOKEN : '',
        center: [lng, lat],
        zoom: zoom,
        maxZoom: 21,
        // Attribution is rendered by <MapAttribution /> in the bottom-right control stack
        attributionControl: false,
        maplibreLogo: false
      });

      // Apply settings like disabling rotating the map
      applyMapSettings(map.current)

      // Init MapBox utils
      U.init(map.current, maplibregl);
      
      // Map event handlers
      map.current.on('error', function(e) {
        if(process && process.env.DEBUG) console.log('An error event occurred.', e);
        dispatch({type: 'SHOW_LOADING', payload: false});
      });
      map.current.on('idle', function(e) {
        if(process && process.env.DEBUG) console.log('An idle event occurred.', e);
        dispatch({type: 'SHOW_LOADING', payload: false});
      });
      map.current.on('moveend', function() {
        registerMapView(map.current);
      })
      map.current.on('zoomend', function() {
        registerMapView(map.current);
      })

      // Do a state update if map is loaded
      map.current.on('load', function() {

        // Store map in a global variable
        window['ddMap'] = map.current;

        setDidMapLoad(true)

        addSources(map.current);
        addLayers(map.current);

        setDidInitSourcesAndLayers(true);

        // Add cross image to use for retirement hubs
        map.current.loadImage('https://cdn0.iconfinder.com/data/icons/blueberry/32/delete.png', (err, image) => {
          // The map may have been unmounted (e.g. page switch) while this
          // request was in flight, in which case map.current is null.
          if(err || !map.current || map.current.hasImage('pattern')) return;
          map.current.addImage('pattern', image);
        });

        // Add 'Mijksenaar' hub logo for Beleidshubs, if zoomed out
        map.current.loadImage('https://dashboarddeelmobiliteit.nl/components/MapComponent/hub-icon-mijksenaar.png', (err, image) => {
          if(err || !map.current || map.current.hasImage('hub-icon-mijksenaar')) return;
          map.current.addImage('hub-icon-mijksenaar', image);
        });
      });

      // Disable rotating
      map.current.dragRotate.disable();
      map.current.touchZoomRotate.disableRotation();
    }
    initMap();

    // Cleanup on unmount: when the user navigates to a non-map route, MapPage
    // (and thus MapComponent) unmounts. Properly destroy the MapLibre instance
    // so its WebGL context, DOM nodes, event listeners and tile requests are
    // released. Without this we leak a full MapLibre map per visit.
    //
    // IMPORTANT: in a deleted React subtree, parent effect cleanups run
    // BEFORE the children's cleanups. Several child layers (e.g.
    // DdServiceAreasLayer, DdPolicyHubsLayer) call `map.getLayer` /
    // `map.removeLayer` in their own cleanup. After `map.remove()` the
    // MapLibre instance's internal style is destroyed and those calls throw
    // ("Cannot read properties of undefined (reading 'getLayer')"). We
    // therefore defer the actual `.remove()` to a microtask so child
    // cleanups run against a still-alive map.
    return () => {
      const toRemove = map.current;
      if (!toRemove) return;
      if (window['ddMap'] === toRemove) {
        window['ddMap'] = null;
      }
      map.current = null;
      mapControlsRef.current.forEach((control) => {
        try {
          toRemove.removeControl(control);
        } catch (e) {
          // Control may already have been removed with the map.
        }
      });
      mapControlsRef.current = [];
      queueMicrotask(() => {
        try {
          toRemove.remove();
        } catch (e) {
          // Ignore: MapLibre sometimes throws during teardown if a request
          // was already aborted; the goal is to not leak, not to be perfectly
          // clean.
        }
      });
    };
  }, [
    lng,
    lat,
    zoom,
    mapContainer,
    // dispatch,
    registerMapView
  ])

  // On map load: Zoom to relevant position
  useEffect(() => {
    if(! didMapLoad) return;

    // Get query params
    const queryParams = new URLSearchParams(window.location.search);

    // Get gm_code from URL
    const gm_code = queryParams.get("gm_code");

    // Get zoom, lat and lng from URL
    const zoom = queryParams.get("zoom");
    const lat = queryParams.get("lat");
    const lng = queryParams.get("lng");

    // If zoom and lat/lng are in URL -> navigate to that location
    let zoomTimeoutId: ReturnType<typeof setTimeout> | undefined;
    if(zoom && lat && lng) {
      console.log('ACTION: setZoom and setCenter (MapComponent - if zoom and lat/lng are in URL)');
      zoomTimeoutId = setTimeout(() => {
        // Guard against the component having unmounted (map.current is reset
        // to null in the cleanup of the init-map effect). Without this guard
        // navigating away from a map route within 1s throws
        // "Cannot read properties of null (reading 'setZoom')".
        if (!map.current) return;
        map.current.setZoom(zoom);
        map.current.setCenter([lng, lat]);
      }, 1000);
    }

    // If on Map page and gm_zone is in URL -> navigate to municipality
    else if(gm_code) {
      dispatch({
        type: 'SET_FILTER_GEBIED',
        payload: gm_code
      })
    }

    return () => {
      if (zoomTimeoutId !== undefined) {
        clearTimeout(zoomTimeoutId);
      }
    };
  }, [
    didMapLoad
  ])

  /**
   * SET SOURCES AND LAYERS
  */

  // Set active source
  useEffect(() => {
    if(! didInitSourcesAndLayers) return;
    if(! didMapLoad) return;
    if(! map.current.isStyleLoaded()) return;

    const activateSources = () => {
      props.activeSources.forEach(sourceName => {
        map.current.U.showSource(sourceName);
      });
      Object.keys(sources).forEach((key, idx) => {
        if(props.activeSources.indexOf(key) <= -1) {
          // Don't remove if source does not exist :)
          if(! map.current.isSourceLoaded(key)) return;
          map.current.U.hideSource(key);
        }
      });
    }

    activateSources()
  }, [
    didInitSourcesAndLayers,
    JSON.stringify(props.activeSources),
    mapStyle
  ])

  // Apply active map style on first load
  // This ensures that the map style stored in Redux state is automatically applied
  // when the map first loads, providing a consistent user experience
  useEffect(() => {
    if(! didInitSourcesAndLayers) return;
    if(! didMapLoad) return;
    if(! map.current) return;
    if(! map.current.isStyleLoaded()) return;
    if(! mapStyle) return;
    if(didApplyMapStyle) return; // Prevent applying multiple times

    // Validate that the map style is a valid option
    const validStyles = ['base', 'satellite'];
    if(! validStyles.includes(mapStyle)) {
      console.warn(`Invalid map style: ${mapStyle}, defaulting to 'base'`);
      return;
    }

    // Apply the active map style using the hook
    setLayer(mapStyle, 
      (layerName) => {
        console.log(`Applied map style on first load: ${layerName}`);
        setDidApplyMapStyle(true);
      },
      (error) => {
        console.warn('Failed to apply map style on first load:', error);
      }
    );
  }, [
    didInitSourcesAndLayers,
    didMapLoad,
    mapStyle,
    setLayer,
    didApplyMapStyle
  ])

  // Reset didApplyMapStyle when map style changes
  useEffect(() => {
    setDidApplyMapStyle(false);
  }, [mapStyle]);

  // Update street visibility when zooming/moving on satellite (fade streets at neighbourhood level)
  const mapStyleRef = useRef(mapStyle);
  mapStyleRef.current = mapStyle;
  useEffect(() => {
    if (!didMapLoad || !map.current) return;
    const updateStreets = () => {
      if (map.current?.isStyleLoaded() && mapStyleRef.current) {
        updateStreetVisibilityForSatellite(map.current, mapStyleRef.current);
      }
    };
    // Throttle zoom handler with rAF so we update during zoom (smooth fade) without flooding
    let zoomRafId = null;
    const zoomHandler = () => {
      if (zoomRafId == null) {
        zoomRafId = requestAnimationFrame(() => {
          updateStreets();
          zoomRafId = null;
        });
      }
    };
    map.current.on('zoom', zoomHandler);
    map.current.on('zoomend', updateStreets);
    map.current.on('moveend', updateStreets);
    updateStreets(); // Run on mount
    return () => {
      if (zoomRafId != null) cancelAnimationFrame(zoomRafId);
      map.current?.off('zoom', zoomHandler);
      map.current?.off('zoomend', updateStreets);
      map.current?.off('moveend', updateStreets);
    };
  }, [didMapLoad]);

  // Data layer management is now handled by MapPage based on active_data_layers
  // The layers are passed via props.layers and activated by the existing activateLayers logic

  // Set active layers (now handled by MapPage based on active_data_layers)
  useEffect(() => {
    if(! didInitSourcesAndLayers) return;
    if(! map.current) return;

    // Do not activate background layers
    // Get background layers from components/Map/MapUtils/backgroundLayerManager.js
    const backgroundLayers = getAvailableLayers();
    const layersToActivate = props.layers.filter(layer => !Object.values(backgroundLayers).some(backgroundLayer => backgroundLayer.layerId === layer));

    let cancelled = false;

    const run = () => {
      if (cancelled || !map.current) return;
      activateLayers(map.current, layers, layersToActivate);
    };

    if (!map.current.isStyleLoaded()) {
      whenMapStyleReady(map.current, run);
      return () => { cancelled = true; };
    }

    run();
    return () => { cancelled = true; };
  }, [
    didInitSourcesAndLayers,
    JSON.stringify(props.layers)
  ])

  // Re-apply user-defined z-order after layers are (re)activated or the style changes.
  useEffect(() => {
    if (!didInitSourcesAndLayers) return;
    if (!map.current) return;

    applyDataLayerOrderWhenReady(
      map.current,
      dataLayerOrder[displayMode],
      displayMode
    );
  }, [
    didInitSourcesAndLayers,
    displayMode,
    JSON.stringify(dataLayerOrder),
    JSON.stringify(props.layers),
    mapStyle
  ])

  // Defensive cleanup: when the display mode changes away from a page that owns
  // dynamic map layers, make sure those layers are removed. Child components
  // normally clean up themselves, but if the map style was loading during their
  // unmount their cleanup may have been deferred. This effect acts as a safety net.
  useEffect(() => {
    if(! map.current) return;
    if(! didMapLoad) return;

    if (displayMode !== DISPLAYMODE_POLICY_HUBS) {
      // Only remove the native policy hubs source; overlay sources are
      // managed by DdPolicyHubsOverlay itself.
      removeHubsFromMap(map.current);
    }
    // The service_areas source is shared by the native layer and the overlay,
    // so keep it if the servicegebieden overlay is enabled for this page.
    const serviceAreasOverlayEnabled = isOverlayLayerEnabled(
      overlayLayers,
      displayMode,
      DATA_LAYER_ORDER_SERVICE_AREAS
    );
    if (displayMode !== DISPLAYMODE_SERVICE_AREAS && !serviceAreasOverlayEnabled) {
      removeServiceAreasFromMap(map.current);
      removeServiceAreaDeltaFromMap(map.current);
    }
  }, [displayMode, didMapLoad, overlayLayers]);

  // Set vehicles sources
  useEffect(() => {
    if(! didInitSourcesAndLayers) return;
    if(! vehicles.data || !vehicles.data.features) return;
    if(! map.current) return;
    if(! map.current.U) return;

    console.log('Setting vehicles data:', vehicles.data.features.length, 'features');
    
    try {
      if(map.current.getSource('vehicles')) {
        map.current.U.setData('vehicles', vehicles.data);
        console.log('Successfully set vehicles data');
      } else {
        console.warn('vehicles source not found');
      }
      if(map.current.getSource('vehicles-clusters')) {
        map.current.U.setData('vehicles-clusters', vehicles.data);
        console.log('Successfully set vehicles-clusters data');
      } else {
        console.warn('vehicles-clusters source not found');
      }
    } catch (error) {
      console.warn('Failed to set vehicles data:', error);
    }
  }, [
    didInitSourcesAndLayers,
    vehicles.data
  ]);

  // Set zones source
  useEffect(()   => {
    if(! didInitSourcesAndLayers) return;
    if(! zones_geodata || zones_geodata.data.length <= 0) return;
    if(! map.current) return;
    if(! map.current.U) return;

    try {
      map.current.U.setData('zones-geodata', zones_geodata.data);
    } catch (error) {
      console.warn('Failed to set zones data:', error);
    }
  }, [
    didInitSourcesAndLayers,
    zones_geodata,
    zones_geodata.data,
  ]);

  // Set rentals origins source
  useEffect(() => {
    if(! didInitSourcesAndLayers) return;
    if(! rentals || ! rentals.origins || Object.keys(rentals.origins).length <= 0) return;
    if(! map.current) return;
    if(! map.current.U) return;

    try {
      map.current.U.setData('rentals-origins', rentals.origins);
      map.current.U.setData('rentals-origins-clusters', rentals.origins);
    } catch (error) {
      console.warn('Failed to set rentals origins data:', error);
    }
  }, [
    didInitSourcesAndLayers,
    rentals,
    rentals.origins
  ]);

  // Set rentals destinations source
  useEffect(() => {
    if(! didInitSourcesAndLayers) return;
    if(! rentals || ! rentals.destinations || Object.keys(rentals.destinations).length <= 0) return;
    if(! map.current) return;
    if(! map.current.U) return;

    try {
      map.current.U.setData('rentals-destinations', rentals.destinations);
      map.current.U.setData('rentals-destinations-clusters', rentals.destinations);
    } catch (error) {
      console.warn('Failed to set rentals destinations data:', error);
    }
  }, [
    didInitSourcesAndLayers,
    rentals,
    rentals.destinations
  ]);

  /**
   * /SET SOURCES AND LAYERS
  */

  // If area selection (place/zone) changes, navigate to area
  useEffect(() => {
    if(! map.current) return;
    if(! extent || extent.length === 0) {
      return;
    }

    // Do not zoom to place if a specific zone is in the URL
    const hasZoneInUrl = () => {
      const params = new URLSearchParams(window.location.search);
      // Policy hubs page: `selected` means we will zoom to that hub instead
      if (
        window.location.pathname.indexOf('/map/beleidshubs') > -1
        && params.getAll('selected').length > 0
      ) {
        return true;
      }
      // Check if we are on the zones page
      if(window.location.pathname.indexOf('/map/zones/') <= -1 && window.location.pathname.indexOf('/admin/zones/') <= -1) return false;
      // Get geographyId from URL
      const geographyId = pathName.split('/zones/')[1];
      return geographyId ? true : false;
    }
    if(hasZoneInUrl()) return;

    console.log('ACTION: fitBounds (MapComponent - if extent changes)');
    map.current.fitBounds(extent);
    
    // Reset extent action
    dispatch({ type: 'LAYER_SET_ZONES_EXTENT', payload: [] });
  }, [
    extent,
    // dispatch
  ])

  // Init popup logic
  useEffect(() => {
    if(! didInitSourcesAndLayers) return;
    if(! providers) return;

    // Only S&B and providers can see vehicle ID
    const canSeeVehicleId = () => {
      // If user isn't logged in, it's no analyst
      if(! userData || ! userData.user) {
        return false;
      }
      // Return true if user is logged in
      return true;
    }

    const hidePopupProviderTitle =
      location.pathname === '/stats/prestaties-aanbieders' &&
      isOperatorPrestatiesView(aclOperators);

    initPopupLogic(
      map.current,
      providers,
      canSeeVehicleId(),
      filter.datum,
      hidePopupProviderTitle
    );
  }, [
    didInitSourcesAndLayers,
    providers,
    aclOperators,
    filter.datum,
    location.pathname,
  ])

  // Init clusters click handler
  useEffect(() => {
    if(! didInitSourcesAndLayers) return;

    initClusters(map.current)
  }, [
    didInitSourcesAndLayers
  ])

  // Add provider images/icons (batched to avoid blocking the main thread)
  useEffect(() => {
    if (!didMapLoad || !map.current) {
      return;
    }

    let cancelled = false;
    const PROVIDER_IMAGE_BATCH_SIZE = 4;

    const addProviderImage = async(aanbieder) => {
      if (cancelled || !map.current) {
        return;
      }

      const operatorColor = aanbieder.color || getProviderColorForProvider(aanbieder.system_id);
      const baselabel = aanbieder.system_id + (stateLayers.displaymode === 'displaymode-rentals' ? '-r' : '-p');
      const hasOperationalImages = map.current.hasImage(baselabel + ':0');
      const hasNonOperationalImages = map.current.hasImage(baselabel + '-n:0');
      if (
        (stateLayers.displaymode === 'displaymode-rentals' && hasOperationalImages) ||
        (stateLayers.displaymode !== 'displaymode-rentals' && hasOperationalImages && hasNonOperationalImages)
      ) {
        return;
      }

      if (stateLayers.displaymode === 'displaymode-rentals') {
        const rentalMarkers = await getVehicleMarkers_rentals(operatorColor);
        if (cancelled || !map.current) {
          return;
        }
        rentalMarkers.forEach((img, idx) => {
          map.current.addImage(baselabel + `:` + idx, { width: 50, height: 50, data: img});
        });
        return;
      }

      const [operationalMarkers, nonOperationalMarkers] = await Promise.all([
        getVehicleMarkers(operatorColor, false),
        getVehicleMarkers(operatorColor, true),
      ]);
      if (cancelled || !map.current) {
        return;
      }

      operationalMarkers.forEach((img, idx) => {
        map.current.addImage(baselabel + `:` + idx, { width: 50, height: 50, data: img});
      });
      nonOperationalMarkers.forEach((img, idx) => {
        map.current.addImage(baselabel + `-n:` + idx, { width: 50, height: 50, data: img});
      });
    };

    const loadProviderImages = async() => {
      for (let i = 0; i < providers.length; i += PROVIDER_IMAGE_BATCH_SIZE) {
        if (cancelled) {
          return;
        }
        const batch = providers.slice(i, i + PROVIDER_IMAGE_BATCH_SIZE);
        await Promise.all(batch.map((aanbieder) => addProviderImage(aanbieder)));
      }
    };

    loadProviderImages();

    return () => {
      cancelled = true;
    };
  }, [
    didMapLoad,
    providers,
    stateLayers.displaymode
  ]);

  const showSearchBar = () => {
    const viewsToShowSearchBar = [
      'displaymode-policy-hubs',
      'displaymode-rentals',
      'displaymode-service-areas',
      'displaymode-park'
    ];
    return viewsToShowSearchBar.indexOf(stateLayers.displaymode) > -1;
  }

  const shouldShowMapTopControls = () => {
    if (isLoggedIn) {
      return true;
    }
    return showSearchBar();
  }

  return <>
    {/* The map container (HTML element) */}
    <div ref={mapContainer} className={`map flex-1 ${filterbarOpen ? 'filter-open' : ''}`} />
    {/* Isochrone layer */}
    {isLoggedIn ? <IsochroneTools map={map.current} /> : null}
    {/* Attribution (bottom-right control stack) */}
    <MapAttribution map={map.current} />
    {/* Service areas layer */}
    {stateLayers.displaymode === 'displaymode-park' && <DdParkEventsLayer map={map.current} />}
    
    {stateLayers.displaymode === 'displaymode-rentals' && <DdRentalsLayer map={map.current} />}
    {/* H3 layer */}{stateLayers.displaymode === 'displaymode-rentals' && <DdH3HexagonLayer map={map.current} />}
    
    {stateLayers.displaymode === 'displaymode-service-areas' && <DdServiceAreasLayer map={map.current} />}
    {stateLayers.displaymode === 'displaymode-policy-hubs' && <>
      <DdPolicyHubsLayer map={map.current} />
    </>}
    {/* Overlay layers (Andere datalaag) on non-native pages */}
    {stateLayers.displaymode !== DISPLAYMODE_SERVICE_AREAS
      && isOverlayLayerEnabled(overlayLayers, stateLayers.displaymode, DATA_LAYER_ORDER_SERVICE_AREAS)
      && <DdServiceAreasOverlay map={map.current} />}
    {stateLayers.displaymode !== DISPLAYMODE_POLICY_HUBS
      && (isOverlayLayerEnabled(overlayLayers, stateLayers.displaymode, DATA_LAYER_ORDER_HUBS)
        || isOverlayLayerEnabled(overlayLayers, stateLayers.displaymode, DATA_LAYER_ORDER_VERBODSGEBIEDEN))
      && <DdPolicyHubsOverlay map={map.current} />}
    {shouldShowMapTopControls() &&
      <>
        <RightTop>
          <div className="flex gap-2">
            <SearchBar map={map.current} />
            {(
              displayMode !== DISPLAYMODE_START
              && displayMode !== DISPLAYMODE_OTHER
            ) && <SelectLayer />}
          </div>
        </RightTop>
      </>
    }
  </>
}

export {
  MapComponent
}
